import { useState, useEffect } from 'react';
import { ethers } from 'ethers';

// Import the WASM module
import init, { generate_deterministic_keypair } from '../pkg/confidential_transfer_proof_generation.js';

// Import secure key cache
import { secureKeyCache } from '../services/secureKeyCache.js';
import { CHAINS } from '../config/chains';
import {
  signStellarMessage,
  ensureKitInitialized,
  assertWalletSupportsStellarSignMessage,
} from '../services/stellarWalletsKit';

function formatWalletKitError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return typeof err === 'string' ? err : 'Unknown error';
}

function parseWalletSignedMessageBytes(signed: unknown): Uint8Array {
  if (signed == null) {
    throw new Error('Wallet returned an empty signature');
  }
  if (signed instanceof Uint8Array) {
    return assert64ByteSig(signed);
  }
  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(signed as Buffer)) {
    return assert64ByteSig(new Uint8Array(signed as Buffer));
  }
  const s = String(signed).trim();
  if (/^[0-9a-fA-F]{128}$/.test(s)) {
    return assert64ByteSig(ethers.utils.arrayify('0x' + s));
  }
  const b64 = s.replace(/^data:[^;]+;base64,/, '').replace(/\s/g, '');
  try {
    let raw: Uint8Array;
    if (typeof Buffer !== 'undefined') {
      raw = new Uint8Array(Buffer.from(b64, 'base64'));
    } else {
      raw = Uint8Array.from(globalThis.atob(b64), (c) => c.charCodeAt(0));
    }
    return assert64ByteSig(raw);
  } catch {
    throw new Error('Could not decode wallet signature (expected 64 bytes as hex or base64)');
  }
}

function assert64ByteSig(u: Uint8Array): Uint8Array {
  if (u.length === 64) return u;
  throw new Error(`Expected 64-byte Stellar ed25519 signature, got ${u.length}`);
}

function ed25519SignatureToPseudoEip712Hex(edSig: Uint8Array): string {
  const r32 = ethers.utils.arrayify(
    ethers.utils.keccak256(
      ethers.utils.concat([edSig, ethers.utils.toUtf8Bytes('stabletrust|derive-r')])
    )
  );
  const s32 = ethers.utils.arrayify(
    ethers.utils.keccak256(
      ethers.utils.concat([edSig, ethers.utils.toUtf8Bytes('stabletrust|derive-s')])
    )
  );
  const vByte = Uint8Array.of(27);
  const sig65 = ethers.utils.concat([r32, s32, vByte]);
  return ethers.utils.hexlify(sig65).replace(/^0x/i, '');
}

interface KeypairResult {
  public_key: string;
  private_key: string;
}

interface UseDeterministicElGamalKeypairResult {
  keypair: KeypairResult | null;
  loading: boolean;
  error: string | null;
  needsConsent: boolean;
}

export function useDeterministicElGamalKeypair(
  provider: ethers.providers.Web3Provider | null,
  account: string | null,
  chainId: number | null,
  verifyingContract: string | null,
  consentGiven: boolean = false,
  /** When set, derive keys via Freighter/Hana `signMessage` (no ethers signer). */
  stellarWalletOrigin: string | null = null
): UseDeterministicElGamalKeypairResult {
  const [keypair, setKeypair] = useState<KeypairResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [needsConsent, setNeedsConsent] = useState<boolean>(false);

  useEffect(() => {
    const isStellarWallet =
      !!stellarWalletOrigin &&
      ['freighter', 'hana', 'xbull', 'lobstr', 'albedo', 'rabet', 'stellar'].includes(
        stellarWalletOrigin
      );

    if (
      !account ||
      verifyingContract === null ||
      verifyingContract === undefined ||
      verifyingContract === '' ||
      chainId === null ||
      chainId === undefined
    ) {
      setKeypair(null);
      setError(null);
      setNeedsConsent(false);
      return;
    }

    if (!isStellarWallet && !provider) {
      setKeypair(null);
      setError(null);
      setNeedsConsent(false);
      return;
    }

    const accountLower = account.toLowerCase();

    const checkAndDeriveKeypair = async () => {
      try {
        if (isStellarWallet) {
          ensureKitInitialized();
          assertWalletSupportsStellarSignMessage();
        }

        console.log('[KeyCache] Initializing secure key cache...');
        await secureKeyCache.initialize();

        console.log('[KeyCache] Checking for cached keypair:', {
          account: accountLower ? `${accountLower.slice(0, 6)}...${accountLower.slice(-4)}` : null,
          chainId,
          verifyingContract: verifyingContract
            ? `${verifyingContract.slice(0, 6)}...${verifyingContract.slice(-4)}`
            : null,
        });
        const cachedKeypair = await secureKeyCache.retrieveKeypair(
          accountLower,
          chainId,
          verifyingContract
        );
        if (cachedKeypair) {
          console.log('[KeyCache] ✓ Found cached keypair, no consent needed');
          secureKeyCache.markAsConnected(accountLower, chainId, verifyingContract);
          setKeypair(cachedKeypair);
          setNeedsConsent(false);
          return;
        }

        console.log('[KeyCache] ✗ No cached keypair found');
        if (!consentGiven) {
          console.log('[KeyCache] No consent given - requesting consent');
          setNeedsConsent(true);
          setKeypair(null);
          return;
        }

        console.log('Consent given, proceeding with key derivation');
        setNeedsConsent(false);
        await deriveKeypair();
      } catch (err) {
        console.error('Error checking cache:', err);
        setError(formatWalletKitError(err));
      }
    };

    const deriveKeypairStellar = async (retryCount = 0) => {
      const MAX_RETRIES = 2;
      setLoading(true);
      setError(null);
      try {
        await init();

        const domainContext = JSON.stringify({
          chainId: chainId.toString(),
          verifyingContract,
          user: accountLower,
          purpose: 'homomorphic-key-derive-v1',
          version: '1',
          scheme: 'stellar-signMessage-v1',
        });

        // Short message for wallet UI: Hana/Freighter often keep Sign disabled until "More details"
        // is opened and scrolled; a huge body makes that worse. Binding is still exact via commitment.
        const commitment = ethers.utils.keccak256(ethers.utils.toUtf8Bytes(domainContext));
        const messageToSign =
          'Stabletrust — Stellar testnet key derivation\n\n' +
          'This only derives encryption keys on this site. No blockchain transaction.\n\n' +
          `Account: ${account!.trim()}\n` +
          `Binding (keccak256): ${commitment}\n`;

        const acct = account!.trim();

        // Unified signing path: route every Stellar wallet (Freighter, Hana,
        // xBull, Albedo, Lobstr, Rabet, WalletConnect, ...) through the
        // Stellar Wallets Kit. SEP-43 `signMessage` returns a 64-byte ed25519
        // signature that the WASM deterministic-keypair routine consumes.
        ensureKitInitialized();
        const res = await signStellarMessage(messageToSign, {
          networkPassphrase: CHAINS.stellar.networkPassphrase,
          address: acct,
        });
        if (!res || !res.signedMessage) {
          throw new Error('Stellar Wallets Kit refused to sign the derivation message');
        }
        const signedMessageRaw: unknown = res.signedMessage;

        const edSig = parseWalletSignedMessageBytes(signedMessageRaw);
        const signatureHex = ed25519SignatureToPseudoEip712Hex(edSig);

        const resultJson = generate_deterministic_keypair(signatureHex, domainContext);
        const result = JSON.parse(resultJson);

        if (!result || !result.private_key || !result.public_key) {
          throw new Error('Keypair derivation failed - invalid result');
        }

        await secureKeyCache.storeKeypair(accountLower, chainId, verifyingContract, result);
        secureKeyCache.markAsConnected(accountLower, chainId, verifyingContract);

        console.log('[Keypair] Derived (Stellar) for user:', accountLower, {
          publicKey: result.public_key,
        });

        setKeypair(result);
      } catch (err) {
        if (retryCount < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, 500 * (retryCount + 1)));
          return deriveKeypairStellar(retryCount + 1);
        }
        console.error('Error deriving Stellar keypair after retries:', err);
        setError(formatWalletKitError(err));
        setKeypair(null);
      } finally {
        setLoading(false);
      }
    };

    const deriveKeypair = async (retryCount = 0) => {
      const MAX_RETRIES = 2;

      if (isStellarWallet) {
        return deriveKeypairStellar(retryCount);
      }

      setLoading(true);
      setError(null);

      try {
        await init();

        let ethereum = null;
        if (provider) {
          ethereum = (provider as any).provider || provider;
        }

        if (!ethereum) {
          ethereum = (window as any).ethereum;
          if (ethereum?.providers) {
            ethereum = ethereum.providers.find((p: any) => p.isMetaMask) || ethereum;
          }
        }

        const isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
          (typeof window !== 'undefined' && window.innerWidth <= 768);

        const maxWaitTime = isMobile ? 2000 : 500;
        let waited = 0;
        let isReady = false;

        if (ethereum) {
          while (!isReady && waited < maxWaitTime) {
            try {
              await ethereum.request({ method: 'eth_chainId' });
              isReady = true;
              console.log('Provider ready for key derivation after', waited, 'ms');
            } catch (e) {
              await new Promise((resolve) => setTimeout(resolve, 100));
              waited += 100;
            }
          }

          if (!isReady) {
            console.warn('Provider readiness check timed out for key derivation, continuing anyway');
          }
        }

        try {
          if (ethereum) {
            await ethereum.request({
              method: 'eth_getBalance',
              params: [accountLower, 'latest'],
            });
          }
        } catch (lockCheckError) {
          console.log('Wallet appears to be locked, triggering unlock for key derivation...');
          if (ethereum) {
            try {
              try {
                await ethereum.request({
                  method: 'wallet_requestPermissions',
                  params: [{ eth_accounts: {} }],
                });
                console.log('Unlocked via wallet_requestPermissions');
              } catch (permError: any) {
                if (
                  permError.code === -32004 ||
                  permError.code === -32601 ||
                  (permError.message && permError.message.includes('not supported'))
                ) {
                  console.log('wallet_requestPermissions not supported, using eth_requestAccounts fallback');
                  await ethereum.request({ method: 'eth_requestAccounts' });
                } else {
                  throw permError;
                }
              }

              await ethereum.request({
                method: 'eth_getBalance',
                params: [accountLower, 'latest'],
              });
            } catch (unlockError) {
              console.warn('Could not unlock wallet, will attempt signing anyway:', unlockError);
            }
          }
        }

        const freshProvider =
          provider || (ethereum ? new ethers.providers.Web3Provider(ethereum) : null);
        if (!freshProvider) {
          throw new Error('No provider available for key derivation');
        }
        const signer = freshProvider.getSigner();

        const context = ethers.utils.keccak256(
          ethers.utils.defaultAbiCoder.encode(
            ['uint256', 'address', 'address', 'string'],
            [chainId, verifyingContract, ethers.constants.AddressZero, 'main']
          )
        );

        const message = {
          purpose: 'homomorphic-key-derive-v1',
          user: accountLower,
          context,
        };

        const signature = await signer._signTypedData(
          { name: 'ConfidentialTokens', version: '1', chainId, verifyingContract },
          {
            DeriveElGamalKey: [
              { name: 'purpose', type: 'string' },
              { name: 'user', type: 'address' },
              { name: 'context', type: 'bytes32' },
            ],
          },
          message
        );

        const signatureHex = signature.slice(2);
        if (!signatureHex.match(/^[0-9a-f]{130}$/i)) {
          throw new Error('Invalid signature format');
        }

        const domainContext = JSON.stringify({
          chainId: chainId.toString(),
          verifyingContract,
          user: accountLower,
          purpose: 'homomorphic-key-derive-v1',
          version: '1',
        });

        const resultJson = generate_deterministic_keypair(signatureHex, domainContext);
        const result = JSON.parse(resultJson);

        if (!result || !result.private_key || !result.public_key) {
          throw new Error('Keypair derivation failed - invalid result');
        }

        await secureKeyCache.storeKeypair(accountLower, chainId, verifyingContract, result);
        secureKeyCache.markAsConnected(accountLower, chainId, verifyingContract);

        console.log('[Keypair] Derived for user:', accountLower, {
          publicKey: result.public_key,
          privateKey: result.private_key,
        });

        setKeypair(result);
      } catch (err) {
        if (retryCount < MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, 500 * (retryCount + 1)));
          return deriveKeypair(retryCount + 1);
        }

        console.error('Error deriving keypair after retries:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setKeypair(null);
      } finally {
        setLoading(false);
      }
    };

    checkAndDeriveKeypair();
  }, [provider, account, chainId, verifyingContract, consentGiven, stellarWalletOrigin]);

  return { keypair, loading, error, needsConsent };
}
